import React, { useState } from 'react';
import moment from 'moment';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    IconButton,
    Tooltip,
    CircularProgress,
    Popover
} from '@mui/material';
import {
    Close as CloseIcon,
    ArrowBackIos,
    ArrowForwardIos
} from '@mui/icons-material';

import { CALENDER_ICON } from 'assets/constants/Icons'; 
import { 
    BOM_STAGES, 
    DELIVERY_CHIP_COLORS, 
    DELIVERY_ROW_BG 
} from 'assets/constants/Constants';
import { getBomList } from 'lib/axiosUtils';

const localizer = momentLocalizer(moment);
const FONT_FAMILY = '"Source Sans Pro", sans-serif'; 

const DeliverySchedule = () => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentDate, setCurrentDate] = useState(new Date());

    const handleOpen = async (event) => {
        if (anchorEl) return;
        
        setAnchorEl(event.currentTarget);
        
        if (events.length === 0) {
            setLoading(true);
            try {
                const res = await getBomList();
                const allBoms = res?.data?.result || [];

                const calendarEvents = allBoms
                    .filter(bom => bom.mfg_delivery_date)
                    .map(bom => {
                        const dateObj = moment(bom.mfg_delivery_date);
                        if (!dateObj.isValid()) return null;

                        return {
                            title: `${bom.customerName || 'Unknown'}`,
                            start: dateObj.toDate(),
                            end: dateObj.toDate(),
                            allDay: true,
                            resource: bom 
                        };
                    })
                    .filter(Boolean);

                setEvents(calendarEvents);
            } catch (error) {
                console.error("Failed to load schedule", error);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    const handleNavigate = (newDate) => {
        setCurrentDate(newDate);
    };

    const CustomEvent = ({ event }) => {
        const { customerName, fixture_document_number, machine, current_stage, current_tracking_status } = event.resource;
        
        const tooltipContent = (
            <Box sx={{ p: 0.5 }}>
                <div style={{ fontWeight: 'bold', borderBottom: '1px solid #eee', marginBottom: '5px', fontSize: '10px' }}>
                    {customerName}
                </div>
                <div style={{ fontSize: '10px' }}>Fixture: {fixture_document_number}</div>
                <div style={{ fontSize: '10px' }}>Machine: {machine || 'N/A'}</div>
                <div style={{ fontSize: '10px' }}>Stage: {current_stage}</div>
                <div style={{ fontSize: '10px' }}>Status: {current_tracking_status || 'N/A'}</div>
            </Box>
        );

        return (
            <Tooltip title={tooltipContent} arrow placement="bottom-end" PopperProps={{ sx: { zIndex: 9999999 } }}>
                <div style={{lineHeight: '1', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    <span style={{ fontSize: '8px'}}>{customerName}</span>
                </div>
            </Tooltip>
        );
    };

    const eventStyleGetter = (event) => {
        const status = event.resource.current_tracking_status;
        const stage = event.resource.current_stage;
        
        let displayStatus = status;
        if (stage === BOM_STAGES.COMPLETED) {
            displayStatus = 'Completed';
        }

        const colorStyle = DELIVERY_ROW_BG[displayStatus] 
            ? DELIVERY_CHIP_COLORS[displayStatus] 
            : { background: '#2d3d42', text: 'white' };

        return {
            style: {
                backgroundColor: colorStyle?.background || '#2d3d42',
                color: colorStyle?.text || 'white',
                borderRadius: '4px',
                border: 'none',
                opacity: 0.9,
                fontSize: '9px',
                minHeight: '10px',
                padding: '0px 2px',
                margin: '1px 0',
                lineHeight: '10px'
            }
        };
    };

    const CustomToolbar = (toolbar) => {
        const goToBack = () => toolbar.onNavigate('PREV');
        const goToNext = () => toolbar.onNavigate('NEXT');
        const date = moment(toolbar.date);
        
        const monthBomCount = (events || []).filter(event =>
            moment(event.start).isSame(date, 'month')
        ).length;

        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 1, p: 0.5, gap: 1 }}>
                <IconButton onClick={goToBack} size="small" sx={{ padding: '2px', border: '0px solid #e0e0e0' }}>
                    <ArrowBackIos sx={{ fontSize: '12px' }} />
                </IconButton>
                <Box>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#2d3d42' }}>
                        {date.format('MMMM YYYY')}
                    </span>
                </Box>
                
                
                <span style={{
                    marginLeft: 4,
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#2d3d42',
                }}>
                    {monthBomCount} Deliveries
                </span>

                <IconButton onClick={goToNext} size="small" sx={{ padding: '2px', border: '0px solid #e0e0e0' }}>
                    <ArrowForwardIos sx={{ fontSize: '12px' }} />
                </IconButton>
            </Box>
        );
    };

    return (
        <>
            <span 
                onClick={handleOpen}
                onMouseEnter={handleOpen}
                aria-describedby={id}
                style={{ 
                    cursor: 'pointer', 
                    userSelect: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                }}
                title="View delivery schedule"
            >
                <i className={`${CALENDER_ICON}`}></i> 
                <span style={{ }}>
                    {moment().format('DD MMM YYYY')}
                </span>
            </span>

            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: { 
                        width: 450,
                        maxHeight: 500,
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        mt: 1.5,
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: '#f9fafb',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                        },
                    }
                }}
            >
                 <Box sx={{ 
                    p: '6px 12px', 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    borderBottom: '1px solid #eee',
                    backgroundColor: '#f9fafb'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <i className={CALENDER_ICON} style={{ fontSize: 14 }}></i>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '11px' }}>
                            Delivery Schedule
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small" sx={{ padding: '2px' }}>
                        <CloseIcon fontSize="small" sx={{ fontSize: 14 }} />
                    </IconButton>
                </Box>

                <Box sx={{ p: 1, overflow: 'hidden' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <Box sx={{ 
                            height: 400, 
                            '& .rbc-calendar': { minHeight: 300 },
                            '& .rbc-toolbar-label': { fontSize: '12px', fontWeight: 700 },
                            '& .rbc-header': { fontSize: '9px', py: 0.5, fontWeight: 600 }, 
                            '& .rbc-date-cell': { fontSize: '9px', fontWeight: 600, padding: '1px 3px' },
                            '& .rbc-event': { 
                                borderRadius: '2px', 
                                padding: '0px 2px',
                                minHeight: '12px !important',
                                fontSize: '8px !important',
                                lineHeight: '12px !important'
                            },
                            '& .rbc-month-row': { minHeight: '40px' },
                            '& .rbc-row-content': { zIndex: 4 },
                            '& .rbc-show-more': { fontSize: '8px', fontWeight: 'bold', color: '#1976d2', marginTop: '1px' },
                            '& .rbc-month-view': { border: '1px solid #eee', borderRadius: '4px' }
                        }}>
                            <Calendar
                                localizer={localizer}
                                events={events}
                                startAccessor="start"
                                endAccessor="end"
                                style={{ height: '100%', fontFamily: FONT_FAMILY }}
                                views={['month']}
                                defaultView="month"
                                date={currentDate}
                                onNavigate={handleNavigate}
                                popup={true}
                                components={{
                                    toolbar: CustomToolbar,
                                    event: CustomEvent,
                                }}
                                eventPropGetter={eventStyleGetter}
                            />
                        </Box>
                    )}
                </Box>
            </Popover>
        </>
    );
};

export default DeliverySchedule;